import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectCreditCardStatisticsActiveBudgetCount } from 'src/app/Account/redux/account.selectors'
import {
  selectCreditCard,
  selectCreditCardsFullPaginatedData,
  selectFilteredCreditCards,
} from 'src/app/AccountBudgets/redux/accountBudgets.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetsBox from 'src/components/BudgetsBox/BudgetsBox'
import { BudgetsFiltersBody } from 'src/components/BudgetsBoxWithFilters/BudgetsBoxWithFilters'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { useBackgroundDispatch, useDebounced } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

const AccountBudgets: React.FC = () => {
  const translate = useTranslate()

  const creditCard = useSelector(selectCreditCard)
  const activeBudgetCount = useSelector(
    selectCreditCardStatisticsActiveBudgetCount,
  )
  const creditCardsFullPaginatedData = useSelector(
    selectCreditCardsFullPaginatedData,
  )
  const filteredCreditCards = useSelector(selectFilteredCreditCards)
  const currentUser = useSelector(selectUserListModelFromState)

  const dispatch = useBackgroundDispatch()

  const fetch = (filtersBody?: BudgetsFiltersBody, page?: number): void => {
    dispatch(
      getCreditCards({
        count: 10,
        page,
        type: CreditCardType.Delegate,
        sortField: 'activeUpdatedAt',
        parentCreditCard: creditCard?.id,
      }),
    )
  }

  const { debouncedF } = useDebounced((): void => {
    fetch()
  }, 0)

  useEffect((): void => {
    debouncedF()
  }, [])

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 122)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 122)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  return creditCard !== undefined && activeBudgetCount !== undefined ? (
    <div
      css={css`
        height: 100%;
      `}
      ref={ref}
      id={'ref'}
    >
      <BackgroundPageContent>
        <BudgetsBox
          budgets={filteredCreditCards}
          isSlim={false}
          clientHeight={clientHeight || 0}
          defaultTitle={
            <ObjectBoxTitle
              title={translate('Budgets Created')}
              static={{
                label: translate('Active'),
                count: activeBudgetCount,
              }}
            />
          }
          infiniteScrollProps={{
            ...creditCardsFullPaginatedData,
            fetch,
          }}
          readOnly={
            creditCard?.ownerId !== currentUser?.id || creditCard.isClosed
          }
        />
      </BackgroundPageContent>
    </div>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default AccountBudgets
